import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { CommandPalette } from '@/components/command-palette/CommandPalette';
import { OnboardingChecklistMount } from '@/components/onboarding/OnboardingChecklistMount';
import { KeyboardShortcutsDialog } from '@/components/ui/keyboard-shortcuts-dialog';
import { type Shortcut, useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { useScreenShortcutsStore } from '@/hooks/use-screen-shortcuts';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/ui';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { TenantSwitcher } from './TenantSwitcher';

export function AppLayout() {
  const { sidebarOpen } = useUiStore();
  const navigate = useNavigate();

  // Command palette and shortcuts dialog state
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);

  /*
    The shell owns the key that opens the dialog, and the screen that is open adds its own
    shortcuts to what the dialog shows. A screen that opened a dialog of its own would hide
    this one from assistive technology, and the reader would get neither.
  */
  const screenShortcuts = useScreenShortcutsStore((state) => state.screen);
  const setShortcutsDialogShared = useScreenShortcutsStore((state) => state.setDialogOpen);

  // The flag outlives this component unless the shell clears it, and a screen that reads a
  // stale "a dialog is open" holds its own keys for good.
  useEffect(() => () => setShortcutsDialogShared(false), [setShortcutsDialogShared]);

  // Define all keyboard shortcuts
  const shortcuts: Shortcut[] = useMemo(
    () => [
      // Command palette
      {
        id: 'command-palette',
        keys: ['k'],
        meta: true,
        action: () => setCommandPaletteOpen(true),
        description: 'Open command palette',
        category: 'general',
      },
      // Shortcuts help
      {
        id: 'show-shortcuts',
        keys: ['?'],
        shift: true,
        action: () => {
          setShortcutsDialogOpen(true);
          setShortcutsDialogShared(true);
        },
        description: 'Show keyboard shortcuts',
        category: 'general',
      },
      // Navigation
      {
        id: 'goto-dashboard',
        keys: ['g', 'd'],
        action: () => navigate('/dashboard'),
        description: 'Go to Dashboard',
        category: 'navigation',
      },
      {
        id: 'goto-projects',
        keys: ['g', 'p'],
        action: () => navigate('/projects'),
        description: 'Go to Projects',
        category: 'navigation',
      },
      {
        id: 'goto-issues',
        keys: ['g', 'i'],
        action: () => navigate('/issues'),
        description: 'Go to Issues',
        category: 'navigation',
      },
      {
        id: 'goto-test-plans',
        keys: ['g', 't'],
        action: () => navigate('/test-plans'),
        description: 'Go to Test Plans',
        category: 'navigation',
      },
      {
        id: 'goto-go-live',
        keys: ['g', 'l'],
        action: () => navigate('/go-live'),
        description: 'Go to Go-Live',
        category: 'navigation',
      },
    ],
    [navigate, setShortcutsDialogShared],
  );

  // Register keyboard shortcuts
  useKeyboardShortcuts({ shortcuts });

  return (
    <>
      <div className="min-h-screen bg-background">
        <Header tenantSwitcher={<TenantSwitcher />} />
        <Sidebar />
        <main
          className={cn(
            'min-h-[calc(100vh-4rem)] pt-16 transition-all duration-300',
            sidebarOpen ? 'md:pl-56' : 'md:pl-16',
          )}
        >
          <div className="container mx-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Command Palette - Cmd+K to open */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onCreateIssue={() => navigate('/issues/new')}
        onCreateProject={() => navigate('/projects/new')}
      />

      {/* Keyboard Shortcuts Dialog - Shift+? to open */}
      <KeyboardShortcutsDialog
        open={shortcutsDialogOpen}
        onOpenChange={(open) => {
          setShortcutsDialogOpen(open);
          setShortcutsDialogShared(open);
        }}
        shortcuts={[...shortcuts, ...(screenShortcuts?.shortcuts ?? [])]}
        heading={screenShortcuts?.heading}
      />

      {/* Onboarding Checklist - bottom-right widget for new customer users */}
      <OnboardingChecklistMount />
    </>
  );
}
